// Re-export RTK Query hooks with cleaner names
export {
  useCancelBundleAssignmentMutation,
  useChangeProviderEmailMutation,
  useGetAppointmentDetailsQuery,
  useGetEncounterDetailsQuery,
  useGetFeaturesListQuery,
  useGetNoteDetailsQuery,
  useGetOrganizationProvidersQuery,
  useGetProviderAppointmentsQuery,
  useGetProviderByIdQuery,
  useGetProviderEncountersQuery,
  useGetProviderInfoQuery,
  useGetProviderNotesQuery,
  useGetProviderQuery,
  useGetProviderTranscriptionsQuery,
  useGetSystemNotificationsConfigQuery,
  useGetTranscriptDetailsQuery,
  useGetUserDetailsQuery,
  useSearchProvidersQuery,
  useUpdateProviderFeatureFlagMutation,
  useUpdateProviderProfileMutation,
  useUpdateSystemNotificationsConfigMutation,
} from './api';

// Import admin, organizations, and container API from generated code
import { enhancedApi as adminApi } from '@store/generated/admin';
import type { HeldRowSchema } from '@store/generated/admin';

import { readHeldResults } from './operation-held-results';
import { enhancedApi as containerApi } from '@store/generated/container';
import { enhancedApi as organizationsApi } from '@store/generated/organizations';

// Re-export data-migration hooks from admin API object
export const {
  useBulkUpdatePatientStatusMutation,
  useDeleteImportSessionMutation,
  useExportImportResultsQuery,
  useGetImportOperationStatusQuery,
  useGetImportSessionStatusQuery,
  useGetMatchedPatientsQuery,
  useImportSelectedPatientsMutation,
  useImportUnmatchedPatientsMutation,
  useListImportOperationsQuery,
  useListOrganizationImportSessionsQuery,
  useMergeMultiplePatientsMutation,
  useRejectPatientsMutation,
} = adminApi;

// Re-export uploadImportFile from our custom implementation
export { useUploadImportFileMutation } from './api';

// Re-export presigned raw-upload hook + contract types (GDN-2196). Hand-authored
// in ./api because this endpoint isn't in the deployed OpenAPI yet.
export type {
  PresignedRawUploadFile,
  PresignRawUploadFileRequest,
  PresignRawUploadsRequest,
  PresignRawUploadsResponse,
} from './api';
export { usePresignRawUploadsMutation } from './api';

// Demo-org provisioning (async via Celery result backend). Endpoints tagged
// ["admin","demo-org"] land in generated/admin.ts; the runtime hooks live on the api object.
export const {
  useProvisionDemoOrgMutation,
  useGetDemoOrgProvisionJobQuery: useGetDemoOrgJobQuery,
  usePreviewDemoOrgProvisionMutation: usePreviewDemoOrgMutation,
  useGetDemoOrgDefaultRosterQuery,
} = adminApi;

// Demo-data seeding (Lane 2 — GDN-2386): copy a pre-converted caseload bundle
// into the org and import it (copy → stage → commit) through the data import
// pipeline. Async via Celery; poll the job for live commit progress. Endpoints
// tagged ["admin","demo-data"] land in generated/admin.ts.
export const { useSeedDemoDataMutation, useGetDemoDataSeedJobQuery } = adminApi;

// Re-export staged-resources (GDN-1947) + conflict-review (GDN-2178) hooks from
// the generated data-migration client.
export type {
  CommitStagedResourcesRequest,
  ConflictItemSchema,
  ConversionJob,
  ConversionLookupResponse,
  PatientMatchCandidateSchema,
  PatientResolution,
  RawConversionFailure,
  RawUploadGroup,
  ResourceImportSessionSummary,
  SessionPatientMatchSchema,
  SessionPatientSchema,
  SourceProviderSchema,
} from '@store/generated/data-migration';
export {
  useCommitStagedResourcesMutation,
  useCreateMigratedProviderMutation,
  useDeleteUnusedRawUploadMutation,
  useDiscoverOrgRawUploadsMutation,
  useGetClassifySummaryQuery,
  useGetStagedResourcesSummaryQuery,
  useLaunchMigrationConversionMutation,
  useListClassifyConflictsQuery,
  useListOrgRawUploadsQuery,
  useListResourceImportSessionsQuery,
  useListSessionPatientMatchesQuery,
  useListSessionPatientsQuery,
  useListSourceProvidersQuery,
  useListStagedResourcesQuery,
  useLookupMigrationConversionsMutation,
  useMatchStagedResourceMutation,
  useRefreshRawUploadInventoryMutation,
  useResolveStagedResourceMutation,
  useSetStagedResourcesProviderMappingMutation,
} from '@store/generated/data-migration';

// Re-export data-export hooks (GDN-1983 — whole-org data export). The list
// endpoint returns each export's live status/progress, so the UI polls the
// list while any export is in flight. Lazy download hook comes off the api
// object (the generated module only named-exports the eager query hooks).
import { enhancedApi as dataExportApi } from '@store/generated/data-export';
export const { useLazyDownloadOrgExportQuery, useListOrgExportsQuery, useStartOrgExportMutation } =
  dataExportApi;
export type {
  ExportFormat,
  ExportListResponse,
  ExportStatusResponse,
} from '@store/generated/data-export';

// Re-export Resource Explorer hooks (GDN-1920/GDN-1928). Sourced from ./api,
// which surfaces them off the generated admin client (tag "admin") — the
// resource routes are tagged ["admin", "resource"], so no separate generated
// slice is needed.
export type {
  AllClassifyConflictsResponse,
  AllSessionPatientMatchesResponse,
  WorklistItem,
  WorklistResponse,
} from './api';
export {
  useAssignEventTagMutation,
  useBrowseStagedResourcesQuery,
  useClearImportSelectionMutation,
  useCreateEventTagMutation,
  // Org billing worklist (GDN-2772/GDN-2774).
  useGetAdminOrganizationWorklistQuery,
  useGetEventCatalogEntryQuery,
  useGetOrganizationActivityQuery,
  useGetRecentActivityQuery,
  useGetResourceActivityQuery,
  useGetResourceDetailQuery,
  useGetResourceTypeSchemaQuery,
  useGetStagedResourceDocumentQuery,
  useGetStagedResourceDocumentUrlQuery,
  useLazyGetRecentActivityQuery,
  useLazyListEventTypeEventsQuery,
  // Data-migration review: fetch every conflict / patient match to completion
  // (GDN-2198), replacing the 500-capped single-page fetches.
  useListAllClassifyConflictsQuery,
  useListAllSessionPatientMatchesQuery,
  // Event catalog (GDN-2681)
  useListEventCatalogQuery,
  useListEventTagsQuery,
  useListResourcesByTypeQuery,
  useListResourceTypesQuery,
  useRequestClassifyMutation,
  // Shared selection working set (GDN-2297): persist + collaborate with the agent.
  useSelectImportRowsMutation,
  // Org-review flag (GDN-2300): orthogonal persisted toggle, server-filterable,
  // shared with the MCP agent — replaces the old `escalate` commit decision.
  useSetOrgReviewMutation,
  // Internal hold (GDN-4065): staff-only, persisted, never reaches the practice.
  useHoldImportRowsMutation,
  useReleaseImportRowHoldsMutation,
  useSyncEventCatalogMutation,
  useUnassignEventTagMutation,
  useUpdateEventCatalogEntryMutation,
} from './api';

// Re-export container admin hooks with user-friendly names
export const {
  useGetV2AdminContainersByContainerKeyEntitiesQuery: useGetContainerEntitiesQuery,
  useGetV2AdminContainersByContainerKeyEntitiesAndEntityIdQuery: useGetContainerEntityDetailQuery,
  useGetV2AdminContainersByContainerKeyEntitiesAndEntityIdComponentsTypeKeyQuery:
    useGetContainerComponentDetailQuery,
} = containerApi;

// Re-export patient intelligence regenerate mutation with user-friendly name
export const {
  usePostV2AdminPatientIntelligenceRegenerateMutation: useRegeneratePatientIntelligenceMutation,
} = adminApi;

// Re-export Prospects (Sales Data) hooks
export {
  useGetBundlesIncludingHiddenQuery,
  useGetProspectByIdQuery,
  useGetProspectsQuery,
  useUploadCsvProspectsMutation,
} from './api';

// Re-export Eligibility Workqueue hooks
export { useAdminLogEligibilityVerificationMutation, useGetAdminEligibilityListQuery } from './api';

// Re-export organizations API hooks with user-friendly names
export const {
  useGetV2AdminOrganizationsQuery,
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  usePatchV2AdminOrganizationsByOrganizationIdMutation,
  useGetV2AdminOrganizationsByOrganizationIdProvidersQuery,
  usePostV2AdminOrganizationsByOrganizationIdMembersMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdMembersAndUserIdMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdReactivateMutation,
  usePatchV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdSyncSubscriptionMutation,
  useGetV2AdminOrganizationsByOrganizationIdSubscriptionDashboardUrlQuery,
  useGetV2AdminOrganizationsByOrganizationIdInvoicesQuery,
  usePostV2AdminOrganizationsByOrganizationIdExtendTrialMutation,
  usePostV2AdminOrganizationsByOrganizationIdCreateSubscriptionMutation,
  usePostV2AdminOrganizationsByOrganizationIdCancelSubscriptionMutation,
  usePostV2AdminOrganizationsByOrganizationIdReactivateSubscriptionMutation,
  usePostV2AdminOrganizationsByOrganizationIdMergeMutation,
  // Billing adjustments: credits, discounts, refunds
  useGetV2AdminOrganizationsByOrganizationIdCreditsQuery,
  usePostV2AdminOrganizationsByOrganizationIdCreditsMutation,
  useGetV2AdminOrganizationsByOrganizationIdDiscountsQuery,
  usePostV2AdminOrganizationsByOrganizationIdDiscountsMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdDiscountsMutation,
  usePostV2AdminOrganizationsByOrganizationIdRefundsMutation,
  usePatchV2AdminOrganizationsByOrganizationIdDiscountsMutation,
  // Templates and Forms
  useGetV2AdminOrganizationsByOrganizationIdTemplatesQuery,
  usePostV2AdminOrganizationsByOrganizationIdTemplatesMutation,
  usePutV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdTemplatesAndTemplateIdMutation,
  useGetV2AdminOrganizationsByOrganizationIdFormsQuery,
  usePostV2AdminOrganizationsByOrganizationIdFormsMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdFormsAndFormIdMutation,
  // Patient Visibility
  useGetV2AdminOrganizationsByOrganizationIdPatientVisibilityQuery,
  usePatchV2AdminOrganizationsByOrganizationIdPatientVisibilityMutation,
  // Role Permissions
  useGetV2AdminOrganizationsByOrganizationIdPermissionsQuery,
  usePatchV2AdminOrganizationsByOrganizationIdPermissionsMutation,
  // Membership Info (co-sign settings)
  useGetV2AdminOrganizationsByOrganizationIdMembersAndUserIdMembershipInfoQuery:
    useGetAdminMembershipInfoQuery,
  usePatchV2AdminOrganizationsByOrganizationIdMembersAndUserIdMembershipInfoMutation:
    useUpdateAdminMembershipInfoMutation,
  // Invitations
  useGetV2AdminOrganizationsByOrganizationIdInvitationsQuery: useGetAdminInvitationsQuery,
  usePostV2AdminOrganizationsByOrganizationIdInvitationsMutation: useCreateAdminInvitationMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdInvitationsAndInvitationIdMutation:
    useDeleteAdminInvitationMutation,
  usePostV2AdminOrganizationsByOrganizationIdInvitationsAndInvitationIdResendMutation:
    useResendAdminInvitationMutation,
} = organizationsApi;

// Re-export admin encounter types hooks with user-friendly names
export const {
  // On-demand reveal of a text input's stored content (admin encounter page)
  useLazyGetV2AdminEncountersByEncounterIdTextInputsAndEncounterDocumentIdQuery:
    useLazyGetEncounterTextInputQuery,
  useGetV2AdminProvidersByProviderIdEncounterTypesQuery: useGetAdminProviderEncounterTypesQuery,
  usePostV2AdminProvidersByProviderIdEncounterTypesMutation:
    useCreateAdminProviderEncounterTypeMutation,
  usePatchV2AdminProvidersByProviderIdEncounterTypesAndEncounterTypeIdMutation:
    useUpdateAdminProviderEncounterTypeMutation,
  useDeleteV2AdminProvidersByProviderIdEncounterTypesAndEncounterTypeIdMutation:
    useDeleteAdminProviderEncounterTypeMutation,
  useGetV2AdminProvidersByProviderIdEncounterTypesAndEncounterTypeIdQuery:
    useGetAdminProviderEncounterTypeQuery,
} = adminApi;

// Re-export org-scoped encounter type hooks with user-friendly names
// Read uses admin route; mutations use provider route (no admin mutations exist yet)
import { enhancedApi as encounterTypesApi } from '@store/generated/encounter-types';

export const {
  useGetV2AdminOrganizationsByOrganizationIdEncounterTypesQuery: useGetOrgEncounterTypesQuery,
  usePostV2ProviderOrganizationsByOrgIdEncounterTypesMutation: useCreateOrgEncounterTypeMutation,
  usePatchV2ProviderOrganizationsByOrgIdEncounterTypesAndEncounterTypeIdMutation:
    useUpdateOrgEncounterTypeMutation,
  useDeleteV2ProviderOrganizationsByOrgIdEncounterTypesAndEncounterTypeIdMutation:
    useDeleteOrgEncounterTypeMutation,
} = encounterTypesApi;

// Re-export admin template discovery API hooks with user-friendly names
export const {
  useGetV2AdminTemplatesUserTemplatesQuery: useGetUserTemplatesQuery,
  // Capabilities hooks (read-only - mutations removed with backend endpoints)
  useGetV2AdminCapabilitiesQuery: useGetCapabilitiesQuery,
  useGetV2AdminCapabilitiesByCapabilityIdQuery: useGetCapabilityQuery,
  // Bundles hooks (read-only - mutations removed with backend endpoints)
  useGetV2AdminBundlesQuery: useGetBundlesQuery,
  useGetV2AdminBundlesByBundleIdQuery: useGetBundleQuery,
  // Bundle assignments hooks
  useGetV2AdminBundleAssignmentsQuery: useGetBundleAssignmentsQuery,
  usePostV2AdminBundleAssignmentsMutation: useCreateBundleAssignmentMutation,
  useGetV2AdminBundleAssignmentsByAssignmentIdQuery: useGetBundleAssignmentQuery,
  usePutV2AdminBundleAssignmentsByAssignmentIdStatusMutation:
    useUpdateBundleAssignmentStatusMutation,
  usePostV2AdminBundleAssignmentsByAssignmentIdReplaceMutation: useReplaceBundleAssignmentMutation,
  // Read-only preview of what a bundle->Stripe sync would change (drift display)
  useGetV2AdminBundleAssignmentsOrganizationsByOrganizationIdSyncBundlesPreviewQuery:
    useGetBundleSyncPreviewQuery,
  // Bundle detail assignments hook (lists all assignments for a specific bundle)
  useGetV2AdminBundlesByBundleIdAssignmentsQuery: useGetBundleDetailAssignmentsQuery,
  // Entitlements hooks
  useGetV2AdminEntitlementsQuery: useGetEntitlementsQuery,
  useGetV2AdminEntitlementsUsageQuery: useGetEntitlementsUsageQuery,
} = adminApi;

// Per-user MCP tokens: hand-authored bridge in ./api (not the generated
// slice) so the app works before the deployed OpenAPI includes the
// endpoints. mcp-access + mint/rotate for everyone; list/revoke admin-only.
export type { McpAccessResponse, McpToolInfo, McpUserTokenInfo } from './api';
export {
  useGetMcpAccessQuery,
  useGetMcpTokensQuery,
  useMintMcpTokenMutation,
  useRevokeMcpTokenMutation,
} from './api';

// Re-export admin build hooks with user-friendly names
export const {
  usePostV2AdminNotetemplatesBuildFromTemplateMutation: useAdminBuildFromTemplateMutation,
  useGetV2AdminNotetemplatesBuildByTaskIdStatusQuery: useAdminBuildStatusQuery,
  usePostV2AdminNotetemplatesBuildByTaskIdSaveMutation: useAdminBuildSaveMutation,
} = adminApi;

// Re-export Stedi transaction hooks with user-friendly names
import { enhancedApi as stediApi } from '@store/generated/stedi';

export const {
  useGetV2AdminOrgsByOrganizationIdStediTransactionsQuery: useStediTransactionsQuery,
  useGetV2AdminOrgsByOrganizationIdStediTransactionsAndTransactionIdQuery:
    useStediTransactionDetailQuery,
} = stediApi;

// Re-export Remittance hooks with user-friendly names
import { enhancedApi as billingApi } from '@store/generated/billing';

export const {
  useGetV2AdminOrgsByOrganizationIdRemittancesQuery: useRemittancesQuery,
  useGetV2AdminOrgsByOrganizationIdRemittancesAndRemittanceIdQuery: useRemittanceDetailQuery,
  useGetV2AdminOrgsByOrganizationIdRemittancesClaimsAndClaimIdSummaryQuery:
    useClaimRemittanceSummaryQuery,
} = billingApi;

// Re-export Billing Dashboard hooks with user-friendly names
export const {
  useGetV2AdminBillingDashboardMorningWorklistQuery: useMorningWorklistQuery,
  useGetV2AdminBillingDashboardMorningWorklistHiddenOrganizationsQuery:
    useHiddenMorningWorklistOrganizationsQuery,
  usePostV2AdminBillingDashboardMorningWorklistHiddenOrganizationsMutation:
    useHideMorningWorklistOrganizationMutation,
  useDeleteV2AdminBillingDashboardMorningWorklistHiddenOrganizationsByOrganizationIdMutation:
    useUnhideMorningWorklistOrganizationMutation,
  useGetV2AdminBillingDashboardOrganizationsQuery: useBillingDashboardOrgsQuery,
  useGetV2AdminBillingDashboardOrganizationsByOrganizationIdQuery:
    useBillingDashboardOrgDetailQuery,
  useGetV2AdminBillingDashboardRcmScoreboardQuery: useRcmScoreboardQuery,
  useGetAdminOrganizationClaimWorkQuery: useAdminClaimWorkQuery,
  useGetAdminOrganizationClaimWorkFilterOptionsQuery: useAdminClaimFilterOptionsQuery,
  useGetAdminOrganizationClaimWorkPatientOptionsQuery: useAdminClaimPatientOptionsQuery,
} = billingApi;

// Billing Task queue hooks (hand-written until the endpoints reach the
// deployed OpenAPI — see billingTasksApi.ts)
export { useBillingTaskOrgsQuery, useOrgBillingTasksQuery } from './billingTasksApi';

// Re-export JotBill Take Config hooks with user-friendly names
export const {
  useGetV2AdminOrgsByOrganizationIdJotbillTakeConfigQuery: useJotBillTakeConfigQuery,
  usePatchV2AdminOrgsByOrganizationIdJotbillTakeConfigMutation: useUpdateJotBillTakeConfigMutation,
} = billingApi;

// Re-export JotBill Fee billing hooks with user-friendly names
export const {
  useGetV2AdminOrgsByOrganizationIdJotbillFeeInvoicesQuery: useJotBillFeeInvoicesQuery,
  useGetV2AdminOrgsByOrganizationIdJotbillFeeInvoicesAndInvoiceIdQuery:
    useJotBillFeeInvoiceDetailQuery,
  usePostV2AdminOrgsByOrganizationIdJotbillFeeRunsMutation: useRunJotBillFeeBillingMutation,
} = billingApi;

// Re-export Sales Scoreboard hooks with user-friendly names
export { useGetSalesScoreboardQuery as useSalesScoreboardQuery } from './salesScoreboardApi';

// Import templates API for model conversion hooks
import { enhancedApi as templatesApi } from '@store/generated/templates';

// Re-export model conversion hooks with user-friendly names
export const {
  usePostV2AdminNotetemplatesModelConversionStartMutation: useStartModelConversionMutation,
  useGetV2AdminNotetemplatesModelConversionStatusByThreadIdQuery: useModelConversionStatusQuery,
  usePostV2AdminNotetemplatesModelConversionSaveMutation: useSaveModelConversionMutation,
  usePostV2AdminNotetemplatesModelConversionCopyToNewMutation: useCopyModelConversionMutation,
} = templatesApi;

// Re-export admin note templates API hooks
export {
  type AdminConvertToOrgRequest,
  type AdminCopyNoteTemplateRequest,
  type AdminCopyNoteTemplateResponse,
  type AdminCreateOrgTemplateRequest,
  type AdminDeleteTemplateResponse,
  type AdminGetNoteTemplateResponse,
  type AdminGetRevisionResponse,
  type AdminListRevisionsResponse,
  // Types
  type AdminNoteTemplateInfo,
  type AdminNoteTemplateListResponse,
  type AdminNoteTemplateSectionRequest,
  type AdminProviderTemplateListResponse,
  type AdminUpdateNoteTemplateRequest,
  type AdminUpdateNoteTemplateResponse,
  // Provider templates types
  type EffectiveNoteTemplateSummary,
  type NoteTemplate2,
  type NoteTemplateOwnerScope,
  type NoteTemplateRevision2,
  type NoteTemplateSection2,
  type RevisionDetail,
  // Revision history types
  type RevisionSummary,
  useConvertAdminNoteTemplateToOrgMutation,
  useCopyAdminNoteTemplateMutation,
  useCreateAdminOrgNoteTemplateMutation,
  useDeleteAdminNoteTemplateMutation,
  // Fork template for user hook (available after codegen)
  useForkTemplateForUserMutation,
  // Single template GET/PUT hooks
  useGetAdminNoteTemplateQuery,
  useGetAdminNoteTemplatesQuery,
  useGetAdminOrgNoteTemplatesQuery,
  // Provider templates hook (what provider actually sees)
  useGetAdminProviderTemplatesQuery,
  useGetAdminTemplateRevisionQuery,
  // Revision history hooks
  useGetAdminTemplateRevisionsQuery,
  useGetAdminUserNoteTemplatesQuery,
  useLazyGetAdminNoteTemplateQuery,
  useLazyGetAdminNoteTemplatesQuery,
  useLazyGetAdminOrgNoteTemplatesQuery,
  useLazyGetAdminTemplateRevisionQuery,
  useLazyGetAdminTemplateRevisionsQuery,
  useLazyGetAdminUserNoteTemplatesQuery,
  // Remove template from user's list
  useRemoveUserTemplateMutation,
  useUpdateAdminNoteTemplateMutation,
} from './noteTemplatesAdminApi';

// Re-export Shadow Notes hooks
export {
  useCreateShadowNoteMutation,
  useDeleteShadowNoteMutation,
  useGetShadowNoteQuery,
  useListShadowNotesQuery,
} from './shadowNotesApi';

// Re-export Shadow Notes types (generated + domain)
export type { ShadowNoteContent, ShadowNoteSection, ShadowNoteStatus } from '../types/shadow-notes';
export type {
  CreateShadowNoteRequest,
  ListShadowNotesParams,
  ShadowNoteListResponse,
  ShadowNoteResponse,
} from './shadowNotesApi';

// Custom hooks for operation tracking
import { useCallback, useEffect, useRef } from 'react';

export interface OperationProgress {
  operationId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: {
    imported_count?: number;
    failed_count?: number;
    skipped_count?: number;
    total_count?: number;
  };
  /**
   * Internal holds (GDN-4065): rows a commit left out because they are held,
   * and children it could not write because a parent is held. A completion
   * snapshot — zero while the run is processing. The counts are complete; the
   * row lists stop at 200. Held-dependency rows are ALSO in `failed_count`.
   */
  held?: {
    count: number;
    rows: HeldRowSchema[];
    dependencyCount: number;
    dependencyRows: HeldRowSchema[];
  };
  error?: string;
  completedAt?: string;
}

export interface UseImportOperationTrackingOptions {
  sessionId: string;
  onOperationComplete?: (operation: OperationProgress) => void;
  onOperationError?: (operation: OperationProgress) => void;
  pollingInterval?: number;
}

export const useImportOperationTracking = ({
  sessionId,
  onOperationComplete,
  onOperationError,
  pollingInterval = 2000,
}: UseImportOperationTrackingOptions) => {
  const activeOperations = useRef<Set<string>>(new Set());
  const pollingTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Get the operation status query hook
  const [getOperationStatus] = adminApi.useLazyGetImportOperationStatusQuery();

  const startTracking = useCallback(
    (operationId: string) => {
      if (activeOperations.current.has(operationId)) {
        return; // Already tracking
      }

      activeOperations.current.add(operationId);

      const poll = async () => {
        try {
          const result = await getOperationStatus({
            sessionId,
            operationId,
          }).unwrap();

          const operation: OperationProgress = {
            operationId,
            status: result.status as OperationProgress['status'],
            progress: {
              imported_count: result.results_imported_count,
              failed_count: result.results_failed_count,
              skipped_count: result.results_skipped_count,
              total_count: result.progress_total_count,
            },
            held: readHeldResults(result),
            error: result.error_details?.[0]?.message || undefined,
            completedAt: result.completed_at || undefined,
          };

          if (result.status === 'completed') {
            // Operation completed successfully
            activeOperations.current.delete(operationId);
            const timer = pollingTimers.current.get(operationId);
            if (timer) {
              clearTimeout(timer);
              pollingTimers.current.delete(operationId);
            }
            onOperationComplete?.(operation);
          } else if (result.status === 'failed') {
            // Operation failed
            activeOperations.current.delete(operationId);
            const timer = pollingTimers.current.get(operationId);
            if (timer) {
              clearTimeout(timer);
              pollingTimers.current.delete(operationId);
            }
            onOperationError?.(operation);
          } else {
            // Still processing, schedule next poll
            const timer = setTimeout(() => {
              void poll();
            }, pollingInterval);
            pollingTimers.current.set(operationId, timer);
          }
        } catch (error) {
          console.error('Error polling operation status:', error);
          // Continue polling on error - the operation might still be running
          const timer = setTimeout(() => {
            void poll();
          }, pollingInterval);
          pollingTimers.current.set(operationId, timer);
        }
      };

      // Start polling immediately
      poll();
    },
    [sessionId, getOperationStatus, onOperationComplete, onOperationError, pollingInterval],
  );

  const stopTracking = useCallback((operationId: string) => {
    activeOperations.current.delete(operationId);
    const timer = pollingTimers.current.get(operationId);
    if (timer) {
      clearTimeout(timer);
      pollingTimers.current.delete(operationId);
    }
  }, []);

  const stopAllTracking = useCallback(() => {
    pollingTimers.current.forEach(timer => clearTimeout(timer));
    pollingTimers.current.clear();
    activeOperations.current.clear();
  }, []);

  const isTracking = useCallback((operationId: string) => {
    return activeOperations.current.has(operationId);
  }, []);

  const getActiveOperations = useCallback(() => {
    return Array.from(activeOperations.current);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllTracking();
    };
  }, [stopAllTracking]);

  return {
    startTracking,
    stopTracking,
    stopAllTracking,
    isTracking,
    getActiveOperations,
  };
};

// Re-export admin semantic-layer (practice metrics) hooks with friendly names.
// Sourced off the generated semantic client (routes tagged ["admin","semantic-metrics"]).
// The dashboard renders the SAME assembler stream the practice owner sees, scoped
// to an explicit org via the URL path; the write records an intervention marker.
import { enhancedApi as semanticMetricsApi } from '@store/generated/semantic';

export const {
  useGetV2AdminSemanticOrganizationsByOrganizationIdAnalyticsContainerQuery:
    useAdminSemanticContainerQuery,
  usePostV2AdminSemanticOrganizationsByOrganizationIdInterventionsMutation:
    useAdminRecordInterventionMutation,
} = semanticMetricsApi;

export { useGetAdminEligibilityRecordQuery, useSaveAdminEligibilityRecordMutation } from './api';
